import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  Schedule,
  Refresh,
} from '@mui/icons-material';
// import { useAuth } from '../contexts/AuthContext';
import axios from '../config/axios';
import dayjs from 'dayjs';
import ModalConfirm from '../components/ModalConfirm';
import ModalResult from '../components/ModalResult';

const MyReservations = () => {
  // const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultTitle, setResultTitle] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [resultSeverity, setResultSeverity] = useState('info');
  const [reservationToCancel, setReservationToCancel] = useState(null);

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const today = dayjs().format('YYYY-MM-DD');
      const response = await axios.get(`/api/reservations?status=active&dateFrom=${today}`);
      setReservations(response.data);
    } catch (err) {
      setError('Errore nel caricamento delle prenotazioni');
      console.error('Error fetching reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
      completed: 'info'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      approved: 'Approvata',
      rejected: 'Rifiutata',
      cancelled: 'Annullata',
      completed: 'Completata'
    };
    return labels[status] || status;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'success',
      refunded: 'info',
      failed: 'error'
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      paid: 'Pagata',
      refunded: 'Rimborsata',
      failed: 'Fallita'
    };
    return labels[status] || status;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return days[dayOfWeek];
  };

  const handleCancelClick = (reservationId) => {
    setReservationToCancel(reservationId);
    setConfirmOpen(true);
  };

  const handleConfirmCancel = async () => {
    setConfirmOpen(false);
    
    try {
      await axios.put(`/api/reservations/${reservationToCancel}/cancel`, {
        reason: 'Annullata dall\'utente'
      });
      await fetchReservations(); // Refresh the list
      
      setResultTitle('Prenotazione Annullata');
      setResultMessage('La prenotazione è stata annullata con successo.');
      setResultSeverity('success');
      setResultOpen(true);
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      setResultTitle('Errore');
      setResultMessage('Si è verificato un errore durante l\'annullamento della prenotazione. Riprova più tardi.');
      setResultSeverity('error');
      setResultOpen(true);
    }
    
    setReservationToCancel(null);
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    setReservationToCancel(null);
  };

  const handleResultClose = () => {
    setResultOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchReservations}
        >
          Riprova
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Le Mie Prenotazioni
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestisci le tue prenotazioni e visualizza lo stato dei pagamenti
          </Typography>
        </Box>
      </Box>

      {reservations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CalendarToday sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nessuna prenotazione trovata
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Non hai ancora effettuato nessuna prenotazione. Vai alla sezione "Prenotazioni" per crearne una.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3} alignItems="stretch">
          {reservations.map((reservation) => (
            <Grid size={{ xs: 12, sm: 6, md: 6 }} key={reservation._id}>
              <Card sx={{ 
                height: '100%', 
                width: '100%',
                display: 'flex', 
                flexDirection: 'column'
              }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {reservation.reservable?.name || 'Risorsa non disponibile'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Stato:
                          </Typography>
                          <Chip
                            label={getStatusLabel(reservation.status)}
                            color={getStatusColor(reservation.status)}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Stato Pagamento:
                          </Typography>
                          <Chip
                            label={getPaymentStatusLabel(reservation.paymentStatus)}
                            color={getPaymentStatusColor(reservation.paymentStatus)}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" color="primary">
                        {reservation.totalPrice}€
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarToday sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {getDayName(new Date(reservation.date).getDay())} {dayjs(reservation.date).format('DD-MMM-YY')}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {reservation.startTime} - {reservation.endTime}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Schedule sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDuration(reservation.duration)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOn sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {reservation.reservable?.location || 'Non specificata'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {reservation.notes && (
                    <Box sx={{ mt: 2, maxHeight: 60, overflow: 'hidden' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        <strong>Note:</strong> {reservation.notes}
                      </Typography>
                    </Box>
                  )}

                  {reservation.adminNotes && (
                    <Box sx={{ mt: 2, maxHeight: 60, overflow: 'hidden' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        <strong>Note amministratore:</strong> {reservation.adminNotes}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 'auto'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Richiesta creata il {dayjs(reservation.createdAt).format('DD-MMM-YY HH:mm')}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {reservation.status === 'pending' && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleCancelClick(reservation._id)}
                        >
                          Annulla
                        </Button>
                      )}
                      
                      {reservation.status === 'approved' && reservation.paymentStatus === 'pending' && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                        >
                          Paga Online
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modal di conferma */}
      <ModalConfirm
        open={confirmOpen}
        title="Conferma Annullamento"
        message="Sei sicuro di voler annullare questa prenotazione? Questa azione non può essere annullata."
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelConfirm}
      />

      {/* Modal di risultato */}
      <ModalResult
        open={resultOpen}
        onClose={handleResultClose}
        title={resultTitle}
        message={resultMessage}
        severity={resultSeverity}
      />
    </Container>
  );
};

export default MyReservations;
